import { LinearProgress } from "@mui/material";
import type { PDFDocumentProxy } from "pdfjs-dist/types/src/display/api";
import type { PropsWithChildren } from "react";
import { useState } from "react";
import { Document } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { getErrorMessage } from "../../../api/axiosHelper";
import { logError } from "../../../logging";
import { sanitizeFileSrcForTelemetry } from "../../../utilities/urlTelemetry";
import PdfDocumentError from "./PdfDocumentError";
import type { PdfViewerState } from "./pdfViewerState";
import { getPdfJsWorkerBuild, initPdfViewer } from "./pdfjsHelper";

interface Props {
  fileSrc: string;
  viewerState: PdfViewerState;
  onDocumentLoad: (documentProxy: PDFDocumentProxy) => void;
  onLoadingProgress: (progress: number) => void;
  onPdfLoadError?: (error: Error, fileSrc: string) => void;
  preventDownload?: boolean;
}

initPdfViewer();

const pdfDocumentOptions = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const PdfDocumentWrapper = ({
  fileSrc,
  viewerState,
  onDocumentLoad,
  onLoadingProgress,
  onPdfLoadError,
  preventDownload,
  children,
}: PropsWithChildren<Props>) => {
  const [loadError, setLoadError] = useState<string>();

  const handleLoadSuccess = (documentProxy: PDFDocumentProxy) => {
    setLoadError(undefined);
    onDocumentLoad(documentProxy);
  };

  const handleLoadError = (error: Error) => {
    const isPdfInitializationError =
      error.message.includes("Setting up fake worker failed") ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed");

    if (isPdfInitializationError) {
      setLoadError("Your browser encountered an issue loading the PDF viewer.");
      return;
    }

    if (navigator.onLine) {
      logError(error, "[PdfDocumentWrapper] onLoadError", {
        pdfPhase: "document-load",
        pdfJsWorkerBuild: getPdfJsWorkerBuild(),
        fileSrc: sanitizeFileSrcForTelemetry(fileSrc),
      });
    }

    setLoadError(getErrorMessage(error));
    onPdfLoadError?.(error, fileSrc);
  };

  const handleLoadProgress = ({ loaded, total }: { loaded: number; total: number }) => {
    const progressPercentage = Math.min((loaded * 100) / total, 100);
    onLoadingProgress(progressPercentage);
  };

  return (
    <Document
      key={fileSrc}
      file={fileSrc}
      options={pdfDocumentOptions}
      rotate={viewerState.rotationDegrees}
      scale={viewerState.scalePercent / 100}
      onLoadSuccess={handleLoadSuccess}
      onLoadError={handleLoadError}
      onLoadProgress={handleLoadProgress}
      loading={<LinearProgress variant="determinate" value={viewerState.loadingProgress} />}
      error={
        <PdfDocumentError
          fileSrc={fileSrc}
          errorMessage="Error loading PDF"
          errorDetails={loadError}
          preventDownload={preventDownload}
        />
      }
      noData={
        <PdfDocumentError fileSrc={fileSrc} errorMessage="Unsupported file format" preventDownload={preventDownload} />
      }
    >
      {children}
    </Document>
  );
};

export default PdfDocumentWrapper;
